import { Atividade } from '../model/processo/Atividade';
import { Cronograma } from '../model/processo/Cronograma';
import { EventoCalendario } from '../view/Cronograma/Calendario/EventoCalendario';
import api from '../api';
import { ProcessoCronograma } from '../view/Cronograma/CronogramaGeral';

export interface ProcessosAtividadesDTO {
    processoNome: string;
    cor: string;
    atividades: Atividade[];
}

// Interface temporária para a resposta do backend
interface ProcessoCronogramaBackend {
  processo: string;         // Nome do processo
  identificador: string;    // ID único do processo
  atividades: AtividadeBackend[];
}

interface AtividadeBackend {
  id: string;
  hora: string;
  data: string;
  cronograma: string;      // ID do cronograma
  unidade: string;
  descricao: string;
  lugar: string;
  _links?: any[];          // Ignoraremos os links
}

// Função de mapeamento principal
function mapearRespostaBackend(
  dadosBackend: ProcessoCronogramaBackend[]
): ProcessoCronograma[] {
  return dadosBackend.map(item => {
    // Mapeia cada atividade
    const atividades: Atividade[] = item.atividades.map(atividade => ({
      id: atividade.id,
      hora: atividade.hora,
      data: atividade.data,
      cronogramaId: atividade.cronograma,
      unidade: atividade.unidade,
      descricao: atividade.descricao,
      lugar: atividade.lugar
    }));

    return {
      processo: {
        id: item.identificador,
        nome: item.processo,
        identificador: item.identificador,
        cor: generateColorFromUUID(item.identificador) // Gera cor baseada no identificador
      },
      cronograma: {
        id: item.atividades[0]?.cronograma || 'default', // Pega o ID do primeiro cronograma
        atividades
      }
    };
  });
}

// utils.ts (arquivo separado)
function generateColorFromUUID(uuid: string): string {
  const hash = uuid.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 70%, 85%)`; // Cor clara para o fundo
}

export class CronogramaViewModel {
    static async obterCronogramasAtivos(processoId: string | undefined): Promise<ProcessosAtividadesDTO[]> {
        if (!processoId) {
            throw new Error('processoId não pode ser undefined');
        }
        
        try {
            const response = await api.get(`/cronogramas/ativos?processoId=${processoId}`);
            return response.data.map((processo: any) => ({
                processoNome: processo.processo,
                cor: processo.cor,
                atividades: processo.atividades.map((atividade: any) => ({
                    id: atividade.id,
                    hora: atividade.hora,
                    data: atividade.data,
                    cronogramaId: atividade.cronograma, 
                    unidade: atividade.unidade,
                    descricao: atividade.descricao,
                    lugar: atividade.lugar,
                })),
            }));
        } catch (error) {
            throw error;
        }
    }

    static async criarAtividadeUsuario(atividade: Atividade | undefined): Promise<Atividade> {
        if (!atividade) {
            throw new Error('atividade não pode ser undefined');
        }
        
        try {
            const response = await api.post(`/atividades`, {
                hora: atividade.hora + ':00',
                data: atividade.data,
                cronograma: atividade.cronogramaId,
                unidade: atividade.unidade,
                descricao: atividade.descricao,
                lugar: atividade.lugar,
            });
    
            return {
                ...atividade,
                id: response.data.id,
            };
        } catch (error) {
            throw error;
        }
    }

    static async criarAtividadesUsuario(eventos: EventoCalendario[] | undefined): Promise<Atividade[]> {
        if (!eventos || eventos.length === 0) {
            throw new Error('eventos não pode ser undefined ou vazio');
        }
        
        try {
            const atividades = eventos.map(evento => ({
                hora: evento.hora + ':00',
                data: evento.data,
                cronograma: evento.cronogramaId, 
                unidade: evento.unidade,
                descricao: evento.descricao,
                lugar: evento.lugar,
            }));
    
            const response = await api.post(`/atividades`, atividades);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    static async obterAtividadesUsuario(idProcesso: string | undefined): Promise<Atividade[]> {
        if (!idProcesso) {
            throw new Error('idProcesso não pode ser undefined');
        }
        
        try {
            const response = await api.get(`/processos/${idProcesso}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    static async atualizarAtividadeUsuario(atividade: Atividade | undefined): Promise<Atividade> {
        if (!atividade || !atividade.id) {
            throw new Error('atividade ou id da atividade não pode ser undefined');
        }
        
        try {
            const response = await api.patch(`/atividades/${atividade.id}`, {
                hora: atividade.hora + ':00',
                data: atividade.data,
                cronograma: atividade.cronogramaId,
                unidade: atividade.unidade,
                descricao: atividade.descricao,
                lugar: atividade.lugar,
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    static async deletarAtividadeUsuario(idAtividade: string | undefined): Promise<void> {
        if (!idAtividade) {
            throw new Error('idAtividade não pode ser undefined');
        }
        
        try {
            await api.delete(`/atividades/${idAtividade}`);
        } catch (error) {
            throw error;
        }
    }

    static async obterCronogramaPorId(idCronograma: string | undefined): Promise<Cronograma> {
        if (!idCronograma) {
            throw new Error('idCronograma não pode ser undefined');
        }
        
        try {
            const response = await api.get(`/cronogramas/${idCronograma}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    static async concluirCronograma(processoId: string, cronogramaId: string): Promise<number> {
        const response = await api.patch(`/processos/${processoId}/cronogramas/${cronogramaId}/conclusao`);
        return response.status;
    }

    static async getCronogramaGeral(): Promise<ProcessoCronograma[]> {
        try {
            const response = await api.get(`/cronogramas/geral`);
            
            // Verificação básica da resposta
            if (!Array.isArray(response?.data)) {
                throw new Error('Formato de resposta inválido');
            }
    
            // Aplica o mapeamento
            return mapearRespostaBackend(response.data);
        } catch (error) {
            console.error('Erro ao buscar cronograma geral:', error);
            throw error; // Propaga o erro para quem chamou
        }
    }
}
